import {
  GraduationCap,
  Users,
  BookOpen,
  TrendingUp,
  Award,
  Clock,
  CheckCircle,
  Coins,
  ArrowUpRight,
  UserPlus,
} from "lucide-react";
import UniversityAdminLayout from "../../layouts/UniversityAdminLayout";
import StatusDot from "../../components/common/StatusDot";
import "../../assets/styles/colors.css";

const universityActivity = [
  { icon: UserPlus, label: "Sarah Johnson enrolled in Red Team track", time: "30 min ago", tint: "var(--color-primary)" },
  { icon: CheckCircle, label: "Cohort B completed Network Defense", time: "3 hours ago", tint: "#3b82f6" },
  { icon: Award, label: "Michael Chen earned 3 new certifications", time: "Yesterday", tint: "#f59e0b" },
  { icon: Coins, label: "450 lab credits allocated to Spring intake", time: "Yesterday", tint: "var(--color-primary)" },
  { icon: BookOpen, label: "New course \"Cloud PenTest\" published", time: "2 days ago", tint: "#7c3aed" },
];

const recentStudents = [
  {
    name: "Sarah Johnson",
    initials: "SJ",
    color: "bg-cyan-100 text-cyan-700",
    email: "sarah.j@university.edu",
    enrolledDate: "2 days ago",
    status: "active",
  },
  {
    name: "Michael Chen",
    initials: "MC",
    color: "bg-blue-100 text-blue-700",
    email: "m.chen@university.edu",
    enrolledDate: "5 days ago",
    status: "active",
  },
  {
    name: "Emma Williams",
    initials: "EW",
    color: "bg-purple-100 text-purple-700",
    email: "e.williams@university.edu",
    enrolledDate: "1 week ago",
    status: "active",
  },
  {
    name: "David Brown",
    initials: "DB",
    color: "bg-orange-100 text-orange-700",
    email: "d.brown@university.edu",
    enrolledDate: "2 weeks ago",
    status: "active",
  },
];

const creditsUsage = [
  {
    category: "Lab Sessions",
    used: 2400,
    allocated: 5000,
    percentage: 48,
    color: "bg-(--color-primary)",
  },
  {
    category: "Practice Boxes",
    used: 1800,
    allocated: 3000,
    percentage: 60,
    color: "bg-blue-500",
  },
  {
    category: "Certifications",
    used: 450,
    allocated: 1000,
    percentage: 45,
    color: "bg-purple-500",
  },
];

export default function UniversityAdminDashboard() {
  return (
    <UniversityAdminLayout>
      <div className="px-8 py-8 space-y-8">
        {/* Header */}
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-(--color-text-muted)">
            University Overview
          </div>
          <h1 className="qv-serif mt-2 text-[40px] md:text-[44px] leading-none font-medium tracking-tight text-(--color-text-primary)">
            Welcome back, <span className="text-(--color-primary)">Admin</span>.
          </h1>
          <p className="mt-2 text-sm text-(--color-text-secondary)">
            Manage students, monitor activity, and track credit usage.
          </p>
        </div>

        {/* Hero band: cohort progress + recent activity */}
        <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div
            className="qv-card qv-noise p-6 xl:col-span-2 flex flex-col justify-between"
            style={{
              background:
                "linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 10%, transparent), color-mix(in srgb, var(--color-accent-violet) 6%, transparent) 60%, transparent)",
            }}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-(--color-text-muted) flex items-center gap-2">
                  <StatusDot variant="live" size={7} /> Cohort Progress
                </div>
                <div className="mt-3 flex items-baseline gap-3">
                  <span className="qv-num text-[44px] leading-none text-(--color-text-primary)">
                    71%
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-(--color-primary)">
                    <ArrowUpRight size={15} /> +9%
                  </span>
                </div>
                <p className="mt-2 text-xs text-(--color-text-muted)">
                  Average curriculum completion across 1,248 enrolled students
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-2 text-[11px] text-(--color-text-muted)">
                {["Term", "Year", "All"].map((r, i) => (
                  <span
                    key={r}
                    className={`px-2 py-1 rounded-md ${i === 0 ? "bg-(--color-primary-100) text-(--color-primary) font-semibold" : ""}`}
                  >
                    {r}
                  </span>
                ))}
              </div>
            </div>
            <svg viewBox="0 0 600 90" className="w-full h-[90px] mt-4" preserveAspectRatio="none">
              <defs>
                <linearGradient id="uniHero" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.35" />
                  <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d="M0,76 C40,72 80,70 120,60 C160,52 200,56 240,46 C280,38 320,42 360,32 C400,24 440,30 480,22 C520,16 560,20 600,14 L600,90 L0,90 Z"
                fill="url(#uniHero)"
              />
              <path
                d="M0,76 C40,72 80,70 120,60 C160,52 200,56 240,46 C280,38 320,42 360,32 C400,24 440,30 480,22 C520,16 560,20 600,14"
                fill="none"
                stroke="var(--color-primary)"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>

          <div className="qv-card qv-noise p-6 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-semibold text-(--color-text-primary)">
                Recent Activity
              </div>
              <span className="text-[10.5px] uppercase tracking-[0.18em] text-(--color-primary) flex items-center gap-1.5">
                <StatusDot variant="live" size={6} /> Live
              </span>
            </div>
            <ul className="space-y-3.5 flex-1">
              {universityActivity.map((item, i) => {
                const Icon = item.icon;
                return (
                  <li key={i} className="flex items-start gap-3">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                      style={{ background: `color-mix(in srgb, ${item.tint} 14%, transparent)` }}
                    >
                      <Icon size={13} style={{ color: item.tint }} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13px] text-(--color-text-primary) leading-snug">
                        {item.label}
                      </div>
                      <div className="text-[11px] text-(--color-text-muted)">
                        {item.time}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* Student Statistics */}
        <section>
          <h2 className="text-xs font-semibold  mb-3 text-(--color-text-primary)">
            Student Overview
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {/* Total Students */}
            <div className="qv-card qv-noise p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-(--color-primary)/20 flex items-center justify-center">
                  <GraduationCap size={18} className="text-(--color-primary)" />
                </div>
                <span className="text-sm text-(--color-text-primary)">
                  Total Students
                </span>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="qv-num text-[32px] leading-none text-(--color-text-primary)">
                  1,248
                </span>
              </div>
              <div className="flex items-center gap-1 text-(--color-primary) text-sm">
                <TrendingUp size={14} />
                <span>+12% from last month</span>
              </div>
            </div>

            {/* Active Students */}
            <div className="qv-card qv-noise p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Users size={18} className="text-blue-400" />
                </div>
                <span className="text-sm text-(--color-text-primary)">
                  Active Students
                </span>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="qv-num text-[32px] leading-none text-(--color-text-primary)">
                  892
                </span>
              </div>
              <div className="text-sm text-(--color-text-muted)">
                71% active this month
              </div>
            </div>

            {/* Completed Courses */}
            <div className="qv-card qv-noise p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle size={18} className="text-green-400" />
                </div>
                <span className="text-sm text-(--color-text-primary)">
                  Completed Courses
                </span>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="qv-num text-[32px] leading-none text-(--color-text-primary)">
                  3,456
                </span>
              </div>
              <div className="text-sm text-(--color-text-muted)">
                This semester
              </div>
            </div>

            {/* Certifications Earned */}
            <div className="qv-card qv-noise p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Award size={18} className="text-yellow-400" />
                </div>
                <span className="text-sm text-(--color-text-primary)">
                  Certifications
                </span>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="qv-num text-[32px] leading-none text-(--color-text-primary)">
                  284
                </span>
              </div>
              <div className="flex items-center gap-1 text-(--color-primary) text-sm">
                <TrendingUp size={14} />
                <span>+18% from last month</span>
              </div>
            </div>
          </div>
        </section>

        {/* Credits Overview */}
        <section>
          <h2 className="text-xs font-semibold mb-3 text-(--color-text-primary)">
            Credits Usage
          </h2>
          <div className="qv-card qv-noise p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {creditsUsage.map((item, idx) => (
                <div key={idx}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-(--color-text-primary)">
                      {item.category}
                    </span>
                    <span className="text-xs text-(--color-text-muted)">
                      {item.used}/{item.allocated}
                    </span>
                  </div>
                  <div className="w-full bg-(--color-border) rounded-full h-2 mb-2">
                    <div
                      className={`${item.color} h-2 rounded-full transition-all duration-300`}
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Coins size={14} className="text-(--color-text-muted)" />
                    <span className="text-xs text-(--color-text-muted)">
                      {item.percentage}% used
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom row: Recent Students */}
        <div className="grid grid-cols-1 gap-6">
          {/* Recent Students */}
          <div className="qv-card qv-noise">
            <div className="flex items-center justify-between px-5 py-4 border-b border-(--color-border)">
              <h3 className="text-sm font-semibold text-(--color-text-primary)">
                Recently Enrolled Students
              </h3>
              <button className="text-xs font-medium text-(--color-primary) hover:underline">
                View all
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-(--color-text-primary)">
                    <th className="px-5 py-3 text-left font-bold">
                      Student Name
                    </th>
                    <th className="px-5 py-3 text-left font-bold">Email</th>
                    <th className="px-5 py-3 text-left font-bold">
                      Enrolled Date
                    </th>
                    <th className="px-5 py-3 text-left font-bold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentStudents.map((student, idx) => (
                    <tr
                      key={idx}
                      className="border-t border-(--color-border) hover:bg-(--color-bg-overlay) dark:hover:bg-white/5 transition-colors"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${student.color}`}
                          >
                            {student.initials}
                          </span>
                          <span className="font-medium text-(--color-text-primary)">
                            {student.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-(--color-text-muted)">
                        {student.email}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1.5 text-(--color-text-muted)">
                          <Clock size={14} />
                          <span>{student.enrolledDate}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/15 text-green-400 border border-green-400/30">
                          {student.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </UniversityAdminLayout>
  );
}
